import CaseDeferredModal from "@/components/case-deferred-modal";
import { CaseParty } from "@/components/cases/case-party";
import { ComplexCaseParty } from "@/components/cases/complex-case-party";
import FloatingAction from "@/components/core/actions/floating-action";
import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import DataGrid, { type ColumnDef } from "@/components/core/data-grid";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { userRoles } from "@/constants/user-roles";
import CaseAttachments from "@/features/case-evaluation/case-attachments";
import CaseEvidence from "@/features/case-evaluation/case-evidence";
import CaseInfoView from "@/features/case-evaluation/case-info";
import CasePaymentProof from "@/features/case-evaluation/case-payment-proof";
import { caseMembersQueryOptions, casePartyMembersQueryOptions, evidenceCasePartyMembersQueryOptions } from "@/features/case-record/api/case-member-queries";
import { caseRecordQueryOptions, useCaseRecordStatusMutation } from "@/features/case-record/api/queries/case-records-query";
import { useCaseRecordOverviewMutation } from "@/features/case-record/api/queries/case-records-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { CaseBlock, CaseBlockBody, CaseBlockHead, CaseLayout } from "@/features/new-case-ui/case-layout";
import { HasRole } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import { IconAlertCircle, IconDeviceFloppy, IconPencil, IconX } from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_backoffice/case-classification_/$caseClassificationId")({
	loader: ({ context: { queryClient }, params }) => {
		return queryClient.ensureQueryData(caseRecordQueryOptions(params.caseClassificationId));
	},
	component: Page,
});

function Page() {
	const params = Route.useParams();
	const navigate = useNavigate();
	const search = Route.useSearch();
	const userQuery = useSuspenseQuery(caseRecordQueryOptions(params.caseClassificationId));

	const data = userQuery.data;
	const modal = useModal();
	const caseRecordStatusMutation = useCaseRecordStatusMutation(data?.id);

	const casePlaintiffQuery = useQuery(
		caseMembersQueryOptions({
			case_id: data.id,
			role: "Plaintiff",
		}),
	);

	const caseDefendantQuery = useQuery(
		caseMembersQueryOptions({
			case_id: data.id,
			role: "Defendant",
		}),
	);
	const casePlaintiff = casePlaintiffQuery.data;
	const caseDefendant = caseDefendantQuery.data;

	const partyMembersQuery = useSuspenseQuery(
		casePartyMembersQueryOptions({
			case_record_id: data.id,
			role: "Defendant",
		}),
	);
	const partyMembers = partyMembersQuery.data;
	const caseRecordOverviewMutation = useCaseRecordOverviewMutation();

	const openDeferredModal = () => {
		modal.openModal({
			allowClose: false,
			title: "Deferred Raffling",
			description: "This case will be tag as deferred",
			size: "md",
			showHeader: true,
			showFooter: false,
			component: <CaseDeferredModal data={data} />,
		});
	};

	const openConfirmModal = () => {
		const modalId = modal.openModal({
			type: "confirm",
			variant: "green",
			title: "Confirm Case Details",
			description: "This will redirect you to the raffle page.",
			onConfirm: async () => {
				const values = {
					id: data?.id,
					for_raffling: true,
					status: "for_raffling",
				};
				await caseRecordStatusMutation.mutateAsync(values);

				navigate({
					to: `/raffle/create/${data?.id}`,
				});
				modal.closeModal(modalId);
			},
			// title: "Confirm Case Details",
			// description: "This will redirect you to the raffle page.",
			// size: "md",
			// showHeader: true,
			// showFooter: false,
			// component: <CaseClassificationModal data={data} />,
		});
	};

	const caseOverviewSmallClaimColumns: ColumnDef<CaseRecord>[] = [
		{
			id: "case_category",
			label: "Case Category",
			cell: ({ data }) => <span>{data.case_category}</span>,
		},
		{
			id: "case_type",
			label: "Case Type",
			cell: ({ data }) => <span>{data.case_type}</span>,
		},
		{
			id: "case_title",
			label: "Case Title",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [title, setTitle] = useState(data.case_title);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "case_title",
							value: title,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="case_title"
									name="case_title"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.case_title}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "nature_of_case",
			label: "Nature of Action",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [nature, setNature] = useState(data.nature_of_action);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "nature_of_action",
							value: nature,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={nature}
									onChange={(e) => setNature(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.nature_of_action || "N/A"}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_filed",
			label: "Date Filed",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [filedAt, setFiledAt] = useState(data.filed_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "filed_at",
							value: filedAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={filedAt}
									type="datetime-local"
									onChange={(e) => setFiledAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.filed_at, "PP")}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_raffled",
			label: "Date Raffled",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [raffledAt, setRaffledAt] = useState(data.raffled_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "raffled_at",
							value: raffledAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={raffledAt}
									type="datetime-local"
									onChange={(e) => setRaffledAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.raffled_at, "PP")}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_received",
			label: "Date Received",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [receivedAt, setReceivedAt] = useState(data.raffled_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "received_at",
							value: receivedAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="received_at"
									name="received_at"
									value={receivedAt}
									type="datetime-local"
									onChange={(e) => setReceivedAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.received_at, "PP")}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "received_by",
			label: "Received By",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [receivedBy, setReceivedBy] = useState(data.received_by);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "received_by",
							value: receivedBy,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="received_by"
									name="received_by"
									value={receivedBy}
									onChange={(e) => setReceivedBy(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.received_by || "N/A"}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "claim_amount",
			label: "Claim Amount",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [claimAmount, setClaimAmount] = useState(data.claim_amount);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "claim_amount",
							value: claimAmount,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="claim_amount"
									name="claim_amount"
									value={claim_amount}
									onChange={(e) => setClaimAmount(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>PHP {formatNumber(data.claim_amount) || "N/A"}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
	];

	const caseOverviewColumns: ColumnDef<CaseRecord>[] = [
		{
			id: "case_category",
			label: "Case Category",
			cell: ({ data }) => <span>{data.case_category}</span>,
		},
		{
			id: "case_type",
			label: "Case Type",
			cell: ({ data }) => <span>{data.case_type}</span>,
		},
		{
			id: "case_title",
			label: "Case Title",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [title, setTitle] = useState(data.case_title);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "case_title",
							value: title,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="case_title"
									name="case_title"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.case_title}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "nature_of_case",
			label: "Nature of Action",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [nature, setNature] = useState(data.nature_of_action);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "nature_of_action",
							value: nature,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={nature}
									onChange={(e) => setNature(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.nature_of_action || "N/A"}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_filed",
			label: "Date Filed",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [filedAt, setFiledAt] = useState(data.filed_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "filed_at",
							value: filedAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={filedAt}
									type="datetime-local"
									onChange={(e) => setFiledAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.filed_at, "PP")}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_raffled",
			label: "Date Raffled",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [raffledAt, setRaffledAt] = useState(data.raffled_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "raffled_at",
							value: raffledAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={raffledAt}
									type="datetime-local"
									onChange={(e) => setRaffledAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.raffled_at, "PP")}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_received",
			label: "Date Received",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [receivedAt, setReceivedAt] = useState(data.raffled_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "received_at",
							value: receivedAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="received_at"
									name="received_at"
									value={receivedAt}
									type="datetime-local"
									onChange={(e) => setReceivedAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.received_at, "PP")}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "received_by",
			label: "Received By",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [receivedBy, setReceivedBy] = useState(data.received_by);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseClassificationId,
						data: {
							column: "received_by",
							value: receivedBy,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="received_by"
									name="received_by"
									value={receivedBy}
									onChange={(e) => setReceivedBy(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.received_by || "N/A"}</span>
								<HasRole roles={[userRoles.BCC]}>
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									>
										<IconPencil color="green" size={20} stroke={2} />
									</button>
								</HasRole>
							</div>
						)}
					</>
				);
			},
		},
	];

	return (
		<Content pageName="Case Information">
			{/*<Header title="Case Information" />*/}
			<div className="flex flex-col w-full max-w-base">
				<div className="flex flex-col w-full p-6">
					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full gap-2">
							<div className="text-bold flex flex-col w-full">
								{data?.status === "pending" ? (
									<span className=" text-xl text-foreground-dark">
										<Alert type="warning" allowClose={true} title="For Docketing" className="" icon={<IconAlertCircle size={20} stroke={2} />}>
											<div className="flex flex-col w-full">
												<span className="font-semibold">{data.case_title || "-"}</span>
												<span className="">Please verify payments and documents, and Confirm to assign case number to this case.</span>
											</div>
										</Alert>
									</span>
								) : (
									<>
										<span className="font-bold text-xl text-foreground-dark">
											{data?.status === "branch_receiving" && `${data.branch} - `} Case No. {data?.case_number}
										</span>
										<span className="text-base text-foreground/70">{data?.case_title || "-"}</span>
									</>
								)}
							</div>
							<div className="flex flex-row items-center w-full gap-2">
								{/* <div className="inline-flex flex-row w-auto gap-1">
                  <Badge size="sm" color="yellow" className="">
                    <span className="uppercase">
                      {(status === 'branch' ? 'Branch Receiving' : status)}
                    </span>
                  </Badge>
                  <Badge className='uppercase' size="sm" color="red-light">20 Days Left</Badge>
                </div> */}
								{/* <div className="flex w-full h-[1px] bg-border/50" /> */}
							</div>
						</div>
						{data?.status === "inhibit" && (
							<Alert type="default" allowClose={true} title="Quick Action" className="" icon={<IconAlertCircle size={20} stroke={2} />}>
								<div className="flex flex-col w-full">
									<span className="">The receiving branch ({data.branch}) would like to inhibit this case.</span>
								</div>
							</Alert>
						)}
						<CaseLayout>
							<CaseBlock>
								<CaseBlockHead>
									<div className="flex flex-col w-auto">
										<span className="text-base font-bold text-foreground-dark">Case Parties</span>
									</div>
								</CaseBlockHead>
								<CaseBlockBody className="border border-border/50">
									<ComplexCaseParty
										allowPost={data.allow_post_as}
										plaintiffs={data.plaintiffs}
										defendants={data.case_defendants}
										caseData={data}
										parties={Object.entries(partyMembers)}
									/>
								</CaseBlockBody>
							</CaseBlock>
							<CaseBlock>
								<CaseBlockHead>
									<div className="flex flex-col w-auto">
										<span className="text-base font-bold text-foreground-dark">Case Overview</span>
									</div>
								</CaseBlockHead>
								<CaseBlockBody className="border border-border/50">
									<DataGrid data={data} columns={data.case_classification !== "Small Claims" ? caseOverviewColumns : caseOverviewSmallClaimColumns} />
								</CaseBlockBody>
							</CaseBlock>
						</CaseLayout>
						<div className="flex flex-col w-full mt-2 mb-20">
							<Tabs
								defaultValue={search.tabs || "payment"}
								onValueChange={async (value) => {
									await navigate({
										to: `/case-classification/${data?.id}`,
										search: (old) => ({
											...old,
											tabs: value,
										}),
									});
								}}
							>
								<TabsList className="">
									<TabsTrigger value="payment">Payment</TabsTrigger>
									<TabsTrigger value="information">Information</TabsTrigger>
									<TabsTrigger value="attachment">Upload Files</TabsTrigger>
									<TabsTrigger value="evidence">Pieces of Evidence</TabsTrigger>
								</TabsList>
								<TabsContent value="payment">
									<div className="flex flex-col w-full gap-4 mt-4">
										<div className="flex flex-col w-full gap-2">
											<CasePaymentProof
												data={data}
												title="Proof of Payment"
												counts={0}
												allowRemove={true}
												allowPreview={false}
												allowPrint={false}
												allowDownload={false}
											/>
										</div>
										{data?.status === "deferred_raffling" && (
											<Alert type="info" allowClose={true} title="Remarks" className="mt-2" icon={<IconAlertCircle size={20} stroke={2} />}>
												{data.defer_remarks}
											</Alert>
										)}
									</div>
								</TabsContent>
								<TabsContent value="information">
									<div className="flex flex-col w-full mt-2">
										<CaseInfoView data={data} attachment={false} remarks={false} caseNumber={false} payments={false} upload={false} />
									</div>
								</TabsContent>
								<TabsContent value="attachment">
									<div className="flex flex-col w-full gap-4 mt-6">
										<div className="flex flex-col w-full gap-2">
											<CaseAttachments
												title="Case Complaints Documents"
												data={data}
												allowRemove={true}
												allowDownload={true}
												hasAttachments={false}
												counts={0}
												allowPreview={false}
												allowPrint={false}
											/>
										</div>
									</div>
								</TabsContent>
								<TabsContent value="evidence">
									<div className="border border-border/50 mt-4 rounded-md">
										<div className="flex flex-col w-full">
											<CaseEvidence data={data} readonly={true} />
										</div>
									</div>
								</TabsContent>
							</Tabs>
						</div>

						{data?.status !== "branch_receiving" && (
							<div className="flex flex-row w-full gap-2">
								<FloatingAction title="Case Actions" description="Please select appropriate action:">
									<div className="inline-flex flex-row w-auto gap-1">
										{(data?.status === "inhibit" || data?.status === "deferred_raffling") && (
											<Button onClick={async () => await navigate({ to: `/raffle/create/${data?.id}` })} variant="green">
												{data.status === "deferred_raffling" ? "Raffle" : "Re-Raffle"}
											</Button>
										)}

										{data?.status === "for_raffling" && (
											<Button onClick={async () => await navigate({ to: `/raffle/create/${data?.id}` })} variant="green">
												Raffle
											</Button>
										)}

										{data?.status === "occ_pending" && (
											<>
												<Button variant="green" onClick={openConfirmModal}>
													Confirm
												</Button>
												<Button variant="danger" onClick={openDeferredModal}>
													Deferred
												</Button>
											</>
										)}
									</div>
								</FloatingAction>
							</div>
						)}
					</div>
				</div>
			</div>
		</Content>
	);
}
